import { makeAutoObservable, runInAction } from "mobx";
import { ExamHttpRepository } from "./exam_http_repository";
import { ExamModel } from "./exam_model";
import { TaskHttpRepository } from "../task/task_http_repository";
import { TaskValidationModel } from "../task/task_model";

export class ExamStore {
  repository = new ExamHttpRepository();
  tasksRepository = new TaskHttpRepository();
  models: ExamModel[] = [];
  page = 0;
  busy = false;
  ready = false;
  error = "";
  notice = "";
  model: ExamModel = { title: "", taskIds: [] };
  baseline = JSON.stringify(this.model);
  totalScoreInput = "";
  scoreBaseline = "";
  tasks: Record<string, TaskValidationModel | null> = {};
  bank: TaskValidationModel[] = [];
  bankPage = 0;
  query = "";
  appliedQuery = "";
  bankBusy = false;
  bankError = "";
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
  get dirty() {
    return (
      JSON.stringify(this.model) !== this.baseline ||
      this.totalScoreInput !== this.scoreBaseline
    );
  }
  setKind(value: "test" | "variant") {
    this.model.kind = value;
    this.model.year = value === "variant" ? new Date().getFullYear() - 1 : null;
    this.notice = "";
  }
  setYear(value: string) {
    this.model.year = value ? Number(value) : null;
    this.notice = "";
  }
  setTitle(value: string) {
    this.model.title = value;
    this.notice = "";
  }
  setStatus(value: "draft" | "published") {
    this.model.status = value;
    this.notice = "";
  }
  setTotalScore(value: string) {
    this.totalScoreInput = value;
    this.model.totalScore = value.trim() ? Number(value) : null;
    this.notice = "";
  }
  setExpiryEnabled(enabled: boolean) {
    this.model.expiresAt = enabled
      ? new Date(Date.now() + 86400000).toISOString()
      : null;
    this.notice = "";
  }
  setExpiresAt(value: string) {
    const date = new Date(`${value}+03:00`);
    this.model.expiresAt =
      value && Number.isFinite(date.getTime()) ? date.toISOString() : value;
    this.notice = "";
  }
  get expiryInput() {
    if (
      !this.model.expiresAt ||
      !Number.isFinite(Date.parse(this.model.expiresAt))
    )
      return "";
    return new Date(Date.parse(this.model.expiresAt) + 3 * 3600000)
      .toISOString()
      .slice(0, 16);
  }
  get taskScores() {
    const weights = this.model.taskIds.map(
      (id) => this.tasks[id]?.scoreAmount || 0,
    );
    if (
      this.model.totalScore == null ||
      !Number.isFinite(this.model.totalScore)
    )
      return weights;
    const sum = weights.reduce((total, weight) => total + weight, 0);
    const cents = Math.round(this.model.totalScore * 100);
    const shares = weights.map(
      (weight) => cents * (sum ? weight / sum : 1 / weights.length),
    );
    const parts = shares.map(Math.floor);
    const order = shares
      .map((share, index) => ({ index, remainder: share - parts[index] }))
      .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    const remaining = cents - parts.reduce((total, part) => total + part, 0);
    for (let index = 0; index < remaining && order.length; index++)
      parts[order[index % order.length].index]++;
    return parts.map((part) => part / 100);
  }
  setQuery(value: string) {
    this.query = value;
  }
  add(task: TaskValidationModel) {
    if (
      !task._id ||
      this.model.taskIds.includes(task._id) ||
      this.model.taskIds.length >= 500
    )
      return;
    this.tasks[task._id] = task;
    this.model.taskIds.push(task._id);
    this.notice = "";
  }
  removeTask(id: string) {
    this.model.taskIds = this.model.taskIds.filter((value) => value !== id);
    this.notice = "";
  }
  move(index: number, direction: number) {
    const target = index + direction;
    if (target < 0 || target >= this.model.taskIds.length) return;
    const ids = [...this.model.taskIds];
    [ids[index], ids[target]] = [ids[target], ids[index]];
    this.model.taskIds = ids;
    this.notice = "";
  }
  async list(page = this.page) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    const result = await this.repository.getPage(page);
    runInAction(() => {
      this.busy = false;
      if (result.isFailure()) this.error = result.error.message;
      else {
        this.models = result.value;
        this.page = page;
      }
    });
  }
  async deleteExam(id: string) {
    if (this.busy) return false;
    this.busy = true;
    this.error = "";
    const result = await this.repository.remove(id);
    runInAction(() => {
      this.busy = false;
      if (result.isFailure()) this.error = result.error.message;
    });
    if (result.isFailure()) return false;
    await this.list(
      this.models.length === 1 ? Math.max(0, this.page - 1) : this.page,
    );
    return true;
  }
  async load(id?: string) {
    if (this.busy) return;
    this.busy = true;
    this.ready = false;
    this.error = "";
    let model: ExamModel = {
      title: "",
      taskIds: [],
      totalScore: 100,
      expiresAt: null,
    };
    if (id) {
      const result = await this.repository.getOne(id);
      if (result.isFailure()) {
        runInAction(() => {
          this.error = result.error.message;
          this.busy = false;
        });
        return;
      }
      model = result.value;
      if (!Array.isArray(model.taskIds)) {
        runInAction(() => {
          this.error =
            "Старая подборка содержит встроенные задания. Автоматическая миграция не поддерживается.";
          this.busy = false;
        });
        return;
      }
    }
    const tasks: Record<string, TaskValidationModel | null> = {};
    // Limit requests to batches of ten for large collections.
    for (let offset = 0; offset < model.taskIds.length; offset += 10) {
      const results = await Promise.all(
        model.taskIds
          .slice(offset, offset + 10)
          .map(async (taskId) => ({
            taskId,
            result: await this.tasksRepository.getOne(taskId),
          })),
      );
      for (const { taskId, result } of results) {
        if (result.isFailure() && result.error.status !== 404) {
          runInAction(() => {
            this.error = result.error.message;
            this.busy = false;
          });
          return;
        }
        tasks[taskId] = result.isSuccess() ? result.value : null;
      }
    }
    runInAction(() => {
      this.model = {
        _id: model._id,
        title: model.title,
        ...(model.kind ? { kind: model.kind } : {}),
        ...(model.year !== undefined ? { year: model.year } : {}),
        taskIds: [...model.taskIds],
        ...(model.status ? { status: model.status } : {}),
        ...(model.totalScore !== undefined
          ? { totalScore: model.totalScore }
          : {}),
        ...(model.expiresAt !== undefined
          ? { expiresAt: model.expiresAt }
          : {}),
      };
      this.totalScoreInput =
        model.totalScore == null ? "" : String(model.totalScore);
      this.scoreBaseline = this.totalScoreInput;
      this.tasks = tasks;
      this.baseline = JSON.stringify(this.model);
      this.busy = false;
      this.ready = true;
    });
    await this.search(0);
  }
  async search(page = 0, applyQuery = true) {
    if (this.bankBusy) return;
    this.bankBusy = true;
    this.bankError = "";
    if (applyQuery) this.appliedQuery = this.query.trim();
    const result = await this.tasksRepository.getPage(
      page,
      this.appliedQuery ? { q: this.appliedQuery } : {},
    );
    runInAction(() => {
      this.bankBusy = false;
      if (result.isFailure()) this.bankError = result.error.message;
      else {
        this.bank = result.value;
        this.bankPage = page;
      }
    });
  }
  async save() {
    if (this.busy || !this.ready) return;
    this.error = "";
    this.notice = "";
    if (!this.model.title.trim() || this.model.title.length > 300) {
      this.error = "Введите название до 300 символов.";
      return;
    }
    if (this.model.taskIds.some((id) => !this.tasks[id])) {
      this.error = "Удалите отсутствующие задания из подборки.";
      return;
    }
    if (
      this.model.totalScore != null &&
      (!Number.isFinite(this.model.totalScore) ||
        this.model.totalScore < 0.01 ||
        this.model.totalScore > 1000000 ||
        Math.abs(
          this.model.totalScore * 100 - Math.round(this.model.totalScore * 100),
        ) > 0.000001)
    ) {
      this.error =
        "Общий балл — от 0,01 до 1 000 000, не более двух знаков после запятой.";
      return;
    }
    if (
      this.model.expiresAt != null &&
      !Number.isFinite(Date.parse(this.model.expiresAt))
    ) {
      this.error = "Укажите дату окончания или выберите бессрочный тест.";
      return;
    }
    if (
      this.model.kind === "variant" &&
      (!Number.isInteger(this.model.year) ||
        (this.model.year || 0) < 1900 ||
        (this.model.year || 0) > 2100)
    ) {
      this.error = "Год варианта — целое число от 1900 до 2100.";
      return;
    }
    this.busy = true;
    const payload = {
      ...this.model,
      title: this.model.title.trim(),
      taskIds: [...this.model.taskIds],
    };
    const result = await this.repository.save(payload);
    runInAction(() => {
      this.busy = false;
      if (result.isFailure()) this.error = result.error.message;
      else {
        this.model = {
          ...payload,
          _id: payload._id || result.value.id || result.value._id,
        };
        this.scoreBaseline = this.totalScoreInput;
        this.baseline = JSON.stringify(this.model);
        this.notice = "Подборка сохранена.";
      }
    });
    return result.isSuccess() ? this.model._id : undefined;
  }
}
